import React, { useEffect, useState } from "react";
import { Spin, Tabs } from "antd";
import { LockOutlined } from "@ant-design/icons";
import isEmpty from "lodash/isEmpty";

import { useSearchContext } from "../contexts/searchcontext.ts";
import CardList from "./cardList";
import RatiosList from "./ratios-list";
import CompletedResultsList from "./CompletedResultsList";
import SavedSearches from "../search/SavedSearches";
import { useUserContext } from "../contexts/usercontext";
import ActiveResultsList from "./ActiveResultsList";
import { useGraphContext } from "../contexts/graphcontext";
import BarChart from "../charts/BarChart";
import { endingAvgPriceModel } from "../charts/barChart.assets";
import { SAVED_SEARCH_TYPES } from "../../globals";
import Services from "../../services";
import PopularCardChart from "../charts/PopularCardChart";
import PopularChartSummary from "../charts/PopularCardChart/PopularChartSummary";
import { usePopularChartContext } from "../contexts/popularchartcontext";
import useGlobal from "../../store";
import { ENTITY_TYPES } from "utils/constants";
import { SHORT_LIST_RANGES } from "../charts/constants";
import {
  MembershipTiers,
  showMembershipTierLimitMessage,
  isUserOfMembershipTier,
  isUserOfMembershipTiers,
} from "utils/user";

export const parseSavedSearchTypeTitle = (savedSearchType) => {
  switch (savedSearchType) {
    case SAVED_SEARCH_TYPES.PLAYER_CHARTS:
      return "Popular Card Charts";
    case SAVED_SEARCH_TYPES.SEALED_WAX_CHARTS:
      return "Popular Wax Charts";
    case SAVED_SEARCH_TYPES.SALES_LOOKUP:
      return "Chart Other Cards";
    case SAVED_SEARCH_TYPES.SET_AND_YEAR:
      return "Set & Year";
    case SAVED_SEARCH_TYPES.SEALED_WAX:
      return "Sealed Wax";
  }
};

const ChartPane = ({
  type = ENTITY_TYPES.CARD,
  showSavedSearches,
  showMarketCapStat,
  savedSearchType,
  hasTabs,
  range,
}) => {
  const searchContext = useSearchContext();
  const { user } = useUserContext();
  const { cards, sealedWaxes } = usePopularChartContext();
  const { setSelected } = useGraphContext();
  const [globalState] = useGlobal();
  const [scrollFunction, setScrollFunction] = useState();
  const [detailedResults, setDetailedResults] = useState({});

  const {
    excludeSellersFromChartResults,
    excludePlatformsFromChartResults,
    excludeNoFeedback,
    excludeNoSellerFeedback,
    excludeLowFeedback,
    excludeSaleWithMakeOffer,
    excludeAuctions,
    excludeBuyItNow,
  } = globalState;

  const { isSearchRunning, initialSearchPerformed, popularCardStats, popularSealedWaxStats } = searchContext;

  const [currentPane, setCurrentPane] = useState(
    savedSearchType === SAVED_SEARCH_TYPES.SET_AND_YEAR ? "endAvgPerEntity" : "valuePerDay",
  );
  const [activeResults, setActiveResults] = useState({});
  const [isLoadingActiveResults, setIsLoadingActiveResults] = useState(false);
  const [activeTab, setActiveTab] = useState("stats");

  const [entities, identifier, getActiveResultsFn, getCompletedResultsFn, popularStats] = {
    [ENTITY_TYPES.CARD]: [cards, "cardId", "getCardActiveResults", "getCardResults", popularCardStats],
    [ENTITY_TYPES.SEALED_WAX]: [
      sealedWaxes,
      "sealedWaxId",
      "getSealedWaxActiveResults",
      "getSealedWaxResults",
      popularSealedWaxStats,
    ],
  }[type];

  useEffect(() => {
    // apply range to the search
    const foundRange = SHORT_LIST_RANGES.find((el) => el.range === range);
    if (foundRange) {
      const { label, value } = foundRange;

      searchContext.setStartDate(value[0]);
      searchContext.setEndDate(value[1]);
      searchContext.setDateRangeLabel(label);
    }

    setScrollFunction(() => {
      const nativeSmoothScrollTo = (a) => {
          window.scroll({ behavior: "smooth", left: 0, top: a.getBoundingClientRect().top + window.pageYOffset });
        },
        smoothScrollTo = function (a, f) {
          var c = document.scrollingElement || document.documentElement,
            d = c.scrollTop,
            g = a - d,
            // eslint-disable-next-line prettier/prettier
            k = +new Date(),
            h = function (e) {
              // eslint-disable-next-line prettier/prettier
              e = +new Date() - k;
              var l = parseInt;
              var b = e / (f / 2);
              1 > b ? (b = (g / 2) * b * b + d) : (b--, (b = (-g / 2) * (b * (b - 2) - 1) + d));
              c.scrollTop = l(b);
              e < f ? requestAnimationFrame(h) : (c.scrollTop = a);
            };
          h();
        },
        supportsNativeSmoothScroll = "scrollBehavior" in document.documentElement.style,
        scrollToElem = (a) => {
          a &&
            (a = document.querySelector(a)) &&
            (supportsNativeSmoothScroll ? nativeSmoothScrollTo(a) : smoothScrollTo(a.offsetTop, 600));
        };
      scrollToElem("#saved-searches-wrapper");
    });
  }, []);

  const getPaginatedActiveAuctions = async (entityId, pagination, sorting) => {
    const result = await Services[getActiveResultsFn](
      entityId,
      pagination.limit,
      pagination.skip * pagination.limit,
      sorting.sortBy,
      sorting.orderBy,
    );
    const newActiveResults = { ...activeResults };
    newActiveResults[entityId] = { data: result.data, total: result.totalCount };
    setActiveResults(newActiveResults);
  };

  const handleDetailedResultsPaginationChange = async (entityId, pagination, sorting, filters) => {
    const result = await fetchCompletedResults(
      entityId,
      pagination.limit,
      pagination.skip,
      sorting.sortBy,
      sorting.orderBy,
      filters.listing_type,
    );
    const newDetailedResults = { ...detailedResults };
    newDetailedResults[entityId] = { data: result.data, total: result.totalCount };
    setDetailedResults(newDetailedResults);
  };

  const getActiveAuctions = async (entities) => {
    setIsLoadingActiveResults(true);

    const results = await Promise.all(
      // TODO: remove hardcoded "buy_it_now_price"
      entities.map((entity) => Services[getActiveResultsFn](entity.id, 10, 0, "buy_it_now_price", "asc")),
    );
    const fullResults = results.reduce((acc, val) => {
      return {
        ...acc,
        [val[identifier]]: { data: val.data, total: val.totalCount },
      };
    }, {});
    setIsLoadingActiveResults(false);
    setActiveResults(fullResults);
  };

  const getDetailedResults = async (entities) => {
    const results = await Promise.all(
      entities.map((entity) => fetchCompletedResults(entity.id, user.is_admin ? 50 : 10, 0, "end_time", "desc")),
    );

    const fullResults = results.reduce((acc, val) => {
      return {
        ...acc,
        [val[identifier]]: { data: val.data, total: val.totalCount },
      };
    }, {});
    setIsLoadingActiveResults(false);
    setDetailedResults(fullResults);
  };

  const handleActiveTabChange = (tabId) => {
    if (tabId === "forSale" && isEmpty(activeResults)) {
      getActiveAuctions(entities);
    }
    if (tabId === "detailedResults" && isEmpty(detailedResults)) {
      getDetailedResults(entities);
    }
    setActiveTab(tabId);
  };

  const fetchCompletedResults = (entityId, limit, skip, sortBy, orderBy, listingTypeFilters) => {
    const { startDate, endDate } = searchContext;

    const params = {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
      excludeSellersFromChartResults,
      excludePlatformsFromChartResults,
      excludeNoFeedback,
      excludeNoSellerFeedback,
      excludeLowFeedback,
      excludeSaleWithMakeOffer,
      excludeAuctions,
      excludeBuyItNow,
      limit,
      skip: skip * limit,
      sortBy,
      orderBy,
      listingTypeFilters,
    };

    switch (type) {
      case ENTITY_TYPES.CARD: {
        params.card_id = entityId;
        break;
      }
      case ENTITY_TYPES.SEALED_WAX: {
        params.sealed_wax_id = entityId;
        break;
      }
      default:
        break;
    }

    return Services[getCompletedResultsFn](params);
  };

  useEffect(() => {
    if (!isEmpty(entities)) {
      getDetailedResults(entities);
      getActiveAuctions(entities);
    }
  }, [
    entities,
    excludeLowFeedback,
    excludeNoFeedback,
    excludeNoSellerFeedback,
    excludeSellersFromChartResults,
    excludePlatformsFromChartResults,
    excludeBuyItNow,
    excludeSaleWithMakeOffer,
    excludeAuctions,
  ]);

  const popularChartContext = usePopularChartContext();
  return (
    <Spin spinning={isSearchRunning} size="large">
      <div className="chartpane-outer-div">
        <div style={{ marginBottom: 16 }}>
          <div>
            <a onClick={scrollFunction} href="#saved-searches" style={{ float: "right" }}>
              Saved Searches
            </a>
            <h2 style={{ marginLeft: "1rem" }}>{parseSavedSearchTypeTitle(savedSearchType)}</h2>
          </div>
        </div>
        <div id="chart">
          {hasTabs && (
            <Tabs
              defaultActiveKey={currentPane}
              onChange={(key) => {
                setCurrentPane(key);
                setSelected(key);
                if (key !== "endAvgPerEntity") popularChartContext.setSelectedGraph(key);
              }}
            >
              <Tabs.TabPane tab="Price Changes ($)" key="valuePerDay" forceRender={true}>
                <PopularCardChart
                  entityType={type}
                  chartId={"valuePerDay"}
                  initialSearchPerformed={initialSearchPerformed}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Price Changes (%)" key="percentageGrowthChart" forceRender={true}>
                <PopularCardChart
                  entityType={type}
                  chartId={"valuePerDay"}
                  initialSearchPerformed={initialSearchPerformed}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Total Sales ($)" key="totalSales" forceRender={true}>
                <PopularCardChart
                  entityType={type}
                  chartId={"totalSales"}
                  initialSearchPerformed={initialSearchPerformed}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="# of Sales" key="numberOfSales" forceRender={true}>
                <PopularCardChart
                  entityType={type}
                  chartId={"numberOfSales"}
                  initialSearchPerformed={initialSearchPerformed}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Last Avg Sale Price" key="endAvgPerEntity" forceRender={true}>
                <BarChart entities={entities} entityType={type} model={endingAvgPriceModel(type)} />
              </Tabs.TabPane>
            </Tabs>
          )}
        </div>
        {initialSearchPerformed && !isEmpty(popularStats) && (
          <div style={{ marginTop: 32 }} className="lockable-tabs">
            <Tabs onChange={handleActiveTabChange} activeKey={activeTab}>
              <Tabs.TabPane tab={"Stats"} key={"stats"}>
                <PopularChartSummary entityType={type} />
                <CardList entityType={type} showMarketCapStat={showMarketCapStat} />
              </Tabs.TabPane>
              <Tabs.TabPane
                key={"ratios"}
                disabled={!(user.membership_tier === "premium" || user.is_pro || user.is_admin)}
                tab={
                  <div
                    onClick={() => {
                      if (user.membership_tier === "starter") {
                        showMembershipTierLimitMessage(
                          "Your plan doesn’t include access to ratios. Upgrade your plan to get access to this feature.",
                        );
                      }
                    }}
                  >
                    Ratios
                    {isUserOfMembershipTier(user, MembershipTiers.STARTER) && (
                      <LockOutlined style={{ marginLeft: 4 }} />
                    )}
                  </div>
                }
              >
                <RatiosList entityType={type} />
              </Tabs.TabPane>
              <Tabs.TabPane tab={"For Sale"} key={"forSale"}>
                <ActiveResultsList
                  onPaginationChange={getPaginatedActiveAuctions}
                  type="fixed"
                  entityType={type}
                  activeResults={activeResults}
                  isLoading={isLoadingActiveResults}
                  getActiveAuctions={getActiveAuctions}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab={"Detailed Results"} key={"detailedResults"}>
                <CompletedResultsList
                  type={type}
                  savedSearchType={savedSearchType}
                  onPaginationChange={handleDetailedResultsPaginationChange}
                  results={detailedResults}
                />
              </Tabs.TabPane>
            </Tabs>
          </div>
        )}
        {showSavedSearches && (
          <div id="saved-searches-wrapper">
            <hr style={{ marginTop: "2rem" }} />
            <div style={{ marginTop: "2rem" }}>
              <SavedSearches type={savedSearchType} anchor="saved-searches" />
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default ChartPane;
